/**
 * ============================================================================
 * APPOINTMENT MODAL v4.2 - BUTTON STRATEGY ALIGNMENT
 * ============================================================================
 *
 * Modal für Termine erstellen/bearbeiten
 *
 * ✅ v4.2: BUTTON STRATEGY ALIGNMENT
 *          - Close Button: 40px → 44px (L4 Icon)
 *          - Footer Buttons: explizite height:44px (L2 Secondary)
 *          - Alle Buttons: lineHeight:1, boxSizing:border-box
 *          - Konsistente Höhen in der Footer-Zeile
 * ✅ v4.1: CSS FIX - borderColor vs border Konflikt behoben
 * ✅ v4.0: CONFLICT DETECTION - Überlappende Termine erkennen
 * ✅ v3.0: Planivio Rebranding
 * ✅ v2.2: FIX - useMemo für PERSONS
 * ✅ v2.1: Dynamische Haushaltsmitglieder
 * ✅ v2.0: Mobile-optimiert (Bottom Sheet)
 *
 * @version 4.2.0 - Button Strategy Alignment
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHousehold } from '../../context/HouseholdContext';
import { useToken } from '../../context/TokenContext';
import ConflictWarning from './ConflictWarning';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ============================================================================
// CONFIGURATION
// ============================================================================

const CATEGORIES = [
  { value: 'privat', label: 'Privat', icon: '🏠', color: '#4A90A4' },
  { value: 'arbeit', label: 'Arbeit', icon: '💼', color: '#8B5CF6' },
  { value: 'arzt', label: 'Arzt', icon: '🏥', color: '#EF4444' },
  { value: 'schule', label: 'Schule', icon: '🎓', color: '#22C55E' },
  { value: 'sport', label: 'Sport', icon: '⚽', color: '#06B6D4' },
  { value: 'familie', label: 'Familie', icon: '👨‍👩‍👧‍👦', color: '#EC4899' },
  { value: 'sonstiges', label: 'Sonstiges', icon: '📌', color: '#6B7280' }
];

const RECURRENCE_OPTIONS = [
  { value: 'none', label: 'Einmalig' },
  { value: 'daily', label: 'Täglich' },
  { value: 'weekly', label: 'Wöchentlich' },
  { value: 'monthly', label: 'Monatlich' },
  { value: 'yearly', label: 'Jährlich' }
];

const REMINDER_OPTIONS = [
  { value: 0, label: 'Keine Erinnerung' },
  { value: 5, label: '5 Minuten vorher' },
  { value: 15, label: '15 Minuten vorher' },
  { value: 30, label: '30 Minuten vorher' },
  { value: 60, label: '1 Stunde vorher' },
  { value: 1440, label: '1 Tag vorher' }
];

// ============================================================================
// STYLES — ✅ v4.2: Button Strategy Alignment
// ============================================================================

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    zIndex: 10000,
    padding: 0
  },
  overlayDesktop: {
    alignItems: 'center',
    padding: '1rem'
  },
  modal: {
    background: '#FFFFFF',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    borderRadius: '24px 24px 0 0',
    boxShadow: '0 -10px 40px rgba(0, 0, 0, 0.2)',
    animation: 'slideUp 0.3s ease'
  },
  modalDesktop: {
    maxWidth: '500px',
    borderRadius: '24px',
    maxHeight: '85vh'
  },
  header: {
    padding: '1.25rem 1.5rem',
    borderBottom: '2px solid #B8D4DC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    background: '#FFFFFF',
    zIndex: 1
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1F2937',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  // ✅ v4.2: Close Button — L4 Icon (44px, 12px radius)
  closeBtn: {
    background: '#FEE2E2',
    border: 'none',
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    fontSize: '1.25rem',
    color: '#EF4444',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    lineHeight: 1,
    boxSizing: 'border-box'
  },
  body: {
    padding: '1.5rem'
  },
  formGroup: {
    marginBottom: '1.25rem'
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '0.5rem'
  },
  required: {
    color: '#EF4444',
    marginLeft: '2px'
  },
  input: {
    width: '100%',
    padding: '0.75rem 1rem',
    border: '2px solid #B8D4DC',
    borderRadius: '12px',
    fontSize: '1rem',
    color: '#1F2937',
    background: '#F0F9FF',
    transition: 'all 0.2s',
    boxSizing: 'border-box'
  },
  inputFocus: {
    border: '2px solid #4A90A4',
    outline: 'none',
    boxShadow: '0 0 0 3px rgba(74, 144, 164, 0.1)'
  },
  select: {
    width: '100%',
    padding: '0.75rem 1rem',
    border: '2px solid #B8D4DC',
    borderRadius: '12px',
    fontSize: '1rem',
    color: '#1F2937',
    background: '#F0F9FF',
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%234A90A4' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")",
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 1rem center',
    paddingRight: '2.5rem'
  },
  textarea: {
    width: '100%',
    padding: '0.75rem 1rem',
    border: '2px solid #B8D4DC',
    borderRadius: '12px',
    fontSize: '1rem',
    color: '#1F2937',
    background: '#F0F9FF',
    minHeight: '80px',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box'
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem'
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    background: '#F0F9FF',
    border: '2px solid #B8D4DC',
    borderRadius: '12px',
    cursor: 'pointer'
  },
  checkboxInput: {
    width: '20px',
    height: '20px',
    accentColor: '#4A90A4'
  },
  footer: {
    padding: '1rem 1.5rem',
    borderTop: '2px solid #B8D4DC',
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'flex-end',
    position: 'sticky',
    bottom: 0,
    background: '#FFFFFF',
    // ✅ v4.2: Alle Footer-Buttons auf gleicher Baseline
    alignItems: 'center'
  },
  footerMobile: {
    flexDirection: 'column'
  },
  // ✅ v4.2: Primary Button — L2 (44px, 12px radius)
  btnPrimary: {
    height: '44px',
    padding: '0 1.5rem',
    background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    transition: 'all 0.2s',
    flex: 1,
    boxShadow: '0 2px 8px rgba(74, 144, 164, 0.3)',
    lineHeight: 1,
    boxSizing: 'border-box',
    whiteSpace: 'nowrap'
  },
  // ✅ v4.2: Secondary Button — L2 (44px, 12px radius, outline)
  btnSecondary: {
    height: '44px',
    padding: '0 1.5rem',
    background: '#FFFFFF',
    color: '#6B7280',
    border: '2px solid #E5E7EB',
    borderRadius: '12px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    boxSizing: 'border-box',
    whiteSpace: 'nowrap'
  },
  // ✅ v4.2: Danger Button — L2 (44px, 12px radius, danger variant)
  btnDanger: {
    height: '44px',
    padding: '0 1.5rem',
    background: '#FEE2E2',
    color: '#DC2626',
    border: '2px solid #FECACA',
    borderRadius: '12px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    transition: 'all 0.2s',
    lineHeight: 1,
    boxSizing: 'border-box',
    whiteSpace: 'nowrap'
  },
  categoryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
    gap: '0.5rem'
  },
  categoryBtn: {
    padding: '0.75rem',
    border: '2px solid #E5E7EB',
    borderRadius: '12px',
    background: '#FFFFFF',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.25rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#6B7280',
    transition: 'all 0.2s'
  },
  categoryBtnActive: {
    border: '2px solid #4A90A4',
    background: '#E8F4F8',
    color: '#4A90A4'
  },
  categoryIcon: {
    fontSize: '1.5rem'
  }
};

// ============================================================================
// COMPONENT
// ============================================================================

function AppointmentModal({
  show,
  onClose,
  onSave,
  onDelete,
  appointment,
  defaultDate
}) {
  const { getMemberOptions, members } = useHousehold();
  const { token } = useToken();

  const PERSONS = useMemo(() => {
    const options = getMemberOptions(true);
    console.log('👥 AppointmentModal: PERSONS updated, count:', options.length);
    return options.map(m => ({
      value: m.id,
      label: m.name,
      icon: m.avatar
    }));
  }, [members, getMemberOptions]);

  const [formData, setFormData] = useState({
    title: '',
    date: defaultDate || new Date().toISOString().split('T')[0],
    time: '10:00',
    endDate: '',
    endTime: '',
    allDay: false,
    recurrence: 'none',
    reminder_minutes: 0,
    category: 'privat',
    person: 'alle',
    location: '',
    description: ''
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [conflicts, setConflicts] = useState([]);
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);
  const [showConflictWarning, setShowConflictWarning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (appointment) {
      setFormData({
        title: appointment.title || '',
        date: appointment.date || defaultDate,
        time: appointment.time?.substring(0, 5) || '10:00',
        endDate: appointment.endDate || '',
        endTime: appointment.endTime || '',
        allDay: appointment.allDay || false,
        recurrence: appointment.recurrence || 'none',
        reminder_minutes: appointment.reminder_minutes || 0,
        category: appointment.category || 'privat',
        person: appointment.person || 'alle',
        location: appointment.location || '',
        description: appointment.description || ''
      });
    } else {
      setFormData({
        title: '',
        date: defaultDate || new Date().toISOString().split('T')[0],
        time: '10:00',
        endDate: '',
        endTime: '',
        allDay: false,
        recurrence: 'none',
        reminder_minutes: 0,
        category: 'privat',
        person: 'alle',
        location: '',
        description: ''
      });
    }
  }, [appointment, defaultDate, show]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'date' || field === 'time' || field === 'allDay') {
      setShowConflictWarning(false);
      setConflicts([]);
    }
  };

  const checkConflicts = useCallback(async () => {
    if (formData.allDay) {
      return { hasConflicts: false, conflicts: [] };
    }

    if (!token || !formData.date || !formData.time) {
      return { hasConflicts: false, conflicts: [] };
    }

    setIsCheckingConflicts(true);

    try {
      const [hours, minutes] = formData.time.split(':').map(Number);
      let endTime;
      if (formData.endTime && (!formData.endDate || formData.endDate === formData.date)) {
        endTime = formData.endTime;
      } else {
        const endHours = hours + 1;
        endTime = `${String(endHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      }

      const params = new URLSearchParams({
        date: formData.date,
        start_time: formData.time,
        end_time: endTime
      });

      if (appointment?.id) {
        params.append('exclude_id', appointment.id);
      }

      const response = await fetch(
        `${API_BASE_URL}/api/calendar/conflicts?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('⚠️ Conflict check result:', data);
        return data;
      }

      return { hasConflicts: false, conflicts: [] };
    } catch (err) {
      console.error('❌ Conflict check error:', err);
      return { hasConflicts: false, conflicts: [] };
    } finally {
      setIsCheckingConflicts(false);
    }
  }, [token, formData.date, formData.time, formData.endDate, formData.endTime, formData.allDay, appointment?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      alert('Bitte geben Sie einen Titel ein.');
      return;
    }

    if (!formData.allDay && !showConflictWarning) {
      const result = await checkConflicts();

      if (result.hasConflicts && result.conflicts.length > 0) {
        setConflicts(result.conflicts);
        setShowConflictWarning(true);
        return;
      }
    }

    doSave();
  };

  const doSave = () => {
    setIsSaving(true);
    onSave({
      ...formData,
      time: formData.allDay ? '00:00' : formData.time
    });
    setShowConflictWarning(false);
    setConflicts([]);
    setIsSaving(false);
  };

  const handleConflictProceed = () => {
    doSave();
  };

  const handleConflictCancel = () => {
    setShowConflictWarning(false);
    setConflicts([]);
  };

  const handleDelete = () => {
    if (appointment?.id) {
      onDelete(appointment.id);
    }
  };

  if (!show) return null;

  const isEditing = !!appointment;

  return (
    <div 
      style={{
        ...styles.overlay,
        ...(isMobile ? {} : styles.overlayDesktop)
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        style={{
          ...styles.modal,
          ...(isMobile ? {} : styles.modalDesktop)
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>
            <span>{isEditing ? '✏️' : '➕'}</span>
            {isEditing ? 'Termin bearbeiten' : 'Neuer Termin'}
          </h2>
          <button 
            style={styles.closeBtn}
            onClick={onClose}
            onMouseEnter={(e) => e.target.style.background = '#FECACA'}
            onMouseLeave={(e) => e.target.style.background = '#FEE2E2'}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div style={styles.body}>

            {/* Conflict Warning */}
            {showConflictWarning && conflicts.length > 0 && (
              <ConflictWarning
                conflicts={conflicts}
                onProceed={handleConflictProceed}
                onCancel={handleConflictCancel}
                isLoading={isSaving}
              />
            )}

            {/* Titel */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Titel <span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                style={styles.input}
                placeholder="z.B. Zahnarzt, Elternabend..."
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
                onBlur={(e) => e.target.style.boxShadow = 'none'}
                required
              />
            </div>

            {/* Datum & Uhrzeit */}
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Datum <span style={styles.required}>*</span>
                </label>
                <input
                  type="date"
                  style={styles.input}
                  value={formData.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Uhrzeit <span style={styles.required}>*</span>
                </label>
                <input
                  type="time"
                  style={styles.input}
                  value={formData.time}
                  onChange={(e) => handleChange('time', e.target.value)}
                  disabled={formData.allDay}
                />
              </div>
            </div>

            {/* End-Datum & End-Uhrzeit */}
            {!formData.allDay && (
              <div style={styles.row}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>End-Datum</label>
                  <input
                    type="date"
                    style={styles.input}
                    value={formData.endDate}
                    min={formData.date}
                    onChange={(e) => handleChange('endDate', e.target.value)}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>End-Uhrzeit</label>
                  <input
                    type="time"
                    style={styles.input}
                    value={formData.endTime}
                    onChange={(e) => handleChange('endTime', e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Ganztägig */}
            <div style={styles.formGroup}>
              <label style={styles.checkbox}>
                <input
                  type="checkbox"
                  style={styles.checkboxInput}
                  checked={formData.allDay}
                  onChange={(e) => handleChange('allDay', e.target.checked)}
                />
                <span>Ganztägig</span>
              </label>
            </div>

            {/* Wiederholung */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Wiederholung</label>
              <select
                style={styles.select}
                value={formData.recurrence}
                onChange={(e) => handleChange('recurrence', e.target.value)}
              >
                {RECURRENCE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Erinnerung */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Erinnerung</label>
              <select
                style={styles.select}
                value={formData.reminder_minutes}
                onChange={(e) => handleChange('reminder_minutes', parseInt(e.target.value))}
              >
                {REMINDER_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Kategorie */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Kategorie</label>
              <div style={styles.categoryGrid}>
                {CATEGORIES.map(cat => (
                  <button
                    key={cat.value}
                    type="button"
                    style={{
                      ...styles.categoryBtn,
                      ...(formData.category === cat.value ? {
                        ...styles.categoryBtnActive,
                        border: `2px solid ${cat.color}`,
                        color: cat.color
                      } : {})
                    }}
                    onClick={() => handleChange('category', cat.value)}
                  >
                    <span style={styles.categoryIcon}>{cat.icon}</span>
                    <span>{cat.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Person */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Person</label>
              <select
                style={styles.select}
                value={formData.person}
                onChange={(e) => handleChange('person', e.target.value)}
              >
                {PERSONS.map(p => (
                  <option key={p.value} value={p.value}>{p.icon} {p.label}</option>
                ))}
              </select>
            </div>

            {/* Ort */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Ort</label>
              <input
                type="text"
                style={styles.input}
                placeholder="z.B. Zahnarztpraxis Dr. Müller"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
              />
            </div>

            {/* Beschreibung */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Notizen</label>
              <textarea
                style={styles.textarea}
                placeholder="Zusätzliche Informationen..."
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
              />
            </div>

          </div>

          {/* Footer */}
          <div style={{
            ...styles.footer,
            ...(isMobile ? styles.footerMobile : {})
          }}>
            {isEditing && (
              <button
                type="button"
                style={styles.btnDanger}
                onClick={handleDelete}
              >
                <span>🗑️</span>
                <span>Löschen</span>
              </button>
            )}
            
            <button
              type="button"
              style={styles.btnSecondary}
              onClick={onClose}
            >
              Abbrechen
            </button>
            
            <button
              type="submit"
              style={{
                ...styles.btnPrimary,
                opacity: (isCheckingConflicts || isSaving) ? 0.7 : 1
              }}
              disabled={isCheckingConflicts || isSaving}
            >
              {isCheckingConflicts ? (
                <>
                  <span style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid white',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                    display: 'inline-block'
                  }} />
                  <span>Prüfe...</span>
                </>
              ) : isSaving ? (
                <>
                  <span style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid white',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                    display: 'inline-block'
                  }} />
                  <span>Speichere...</span>
                </>
              ) : (
                <>
                  <span>{isEditing ? '💾' : '➕'}</span>
                  <span>{isEditing ? 'Speichern' : 'Erstellen'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Animation Keyframes */}
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default AppointmentModal;